import { Injectable, inject } from '@angular/core';
import {
  BridgingLenderProducts,
  ILoadingOptions,
  MatchedLenderProducts,
  ProductCollectionType,
  ProductUploadAdditionalResult,
  ProductUploadType,
  ProductsVisibilityState,
  Schedule,
  ScheduledValuationFeeScaleDateUpdateRequest,
  TrackerProductRateType,
  UploadedProductsResult,
  ValuationFeeScale,
  ValuationFeeScaleUpdateRequest,
} from '@shared/models';
import { FeeScaleConditionType } from '@shared/models/enums/fee-scale-condition-type';
import {
  PackagerVariableRateCodeViewModel,
  VariableRateCodeLookupUpdateRequest,
  VariableRateCodeLookupViewModel,
  VariableRateCodeUsageViewModel,
  VariableRateConfigLookupUpdateRequest,
  VariableRateConfigLookupViewModel,
} from '@shared/models/variable-rate-codes';
import { LenderHubDataService } from '@shared/services/lenderhub-data.service';
import { format } from 'date-fns';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { parseISO } from 'date-fns/parseISO';
import { Observable, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  private readonly lenderHubDataService = inject(LenderHubDataService);

  public unsavedChanges = false;

  public uploadProducts(
    file: File,
    type: ProductCollectionType,
    uploadType: ProductUploadType,
    preview: boolean,
  ): Observable<UploadedProductsResult>;
  public uploadProducts(
    file: File,
    type: ProductCollectionType,
    uploadType: ProductUploadType,
    preview: boolean,
    scheduleInfo: { schedule: string | null; showWithdrawalDateFrom: string | null },
    variableRateCodes: VariableRateCodeLookupUpdateRequest | null,
    hasMissingVariableRateCodes: boolean | null,
  ): Observable<ProductUploadAdditionalResult>;
  public uploadProducts(
    file: File,
    type: ProductCollectionType,
    uploadType: ProductUploadType = ProductUploadType.Core,
    preview: boolean,
    scheduleInfo: { schedule: string | null; showWithdrawalDateFrom: string | null } | null = null,
    variableRateCodes: VariableRateCodeLookupUpdateRequest | null = null,
    hasMissingVariableRateCodes: boolean | null = null,
  ) {
    const formData: FormData = new FormData();
    formData.append('fileKey', file, file.name);

    if (scheduleInfo?.schedule) {
      formData.append('scheduleDateTime', scheduleInfo.schedule);
    }

    if (scheduleInfo?.showWithdrawalDateFrom) {
      formData.append('showWithdrawalDateFrom', scheduleInfo.showWithdrawalDateFrom);
    }

    const formDataAppendObject = (value: unknown, path: string = '') => {
      if (value === undefined || value === null) {
        return;
      }

      if (typeof value === 'object') {
        const entries = Object.entries(value as Record<string, unknown>);

        for (const [key, nestedValue] of entries) {
          formDataAppendObject(nestedValue, path ? `${path}[${key}]` : key);
        }

        return;
      }

      formData.append(path, value.toString());
    };
    formDataAppendObject(variableRateCodes, 'variableRateCodesUpdate');
    formDataAppendObject(hasMissingVariableRateCodes, 'hasMissingVariableRateCodes');
    const uploadUrl = `Product/ProductUpload/${type}?uploadType=${uploadType}${preview ? '&preview=true' : ''}`;

    return this.lenderHubDataService.post<UploadedProductsResult | ProductUploadAdditionalResult>(uploadUrl, formData, null, {
      showErrorToast: false,
    });
  }

  public deleteSchedule(type: ProductCollectionType, scheduleId: string): Observable<void> {
    return this.lenderHubDataService.delete(`Product/DeleteSchedule/${type}?scheduleId=${scheduleId}`);
  }

  public updateSchedule(
    type: ProductCollectionType,
    scheduleId: string,
    data: { scheduleDateTime: string; showWithdrawalDateFrom: string | null },
  ): Observable<void> {
    const url = `Product/UpdateSchedule/${type}?scheduleId=${scheduleId}`;
    return this.lenderHubDataService.post(url, data, null, { showErrorToast: false });
  }

  public getAllBridgingProducts(uploadType: ProductUploadType): Observable<BridgingLenderProducts> {
    return this.getAllProducts(ProductCollectionType.Bridging, uploadType) as Observable<BridgingLenderProducts>;
  }

  public getAllResAndBtlProducts(uploadType: ProductUploadType): Observable<MatchedLenderProducts> {
    return this.getAllProducts(ProductCollectionType.ResiBtl, uploadType) as Observable<MatchedLenderProducts>;
  }

  public getAllProducts(
    type: ProductCollectionType,
    uploadType: ProductUploadType,
  ): Observable<MatchedLenderProducts | BridgingLenderProducts> {
    return this.lenderHubDataService.get<MatchedLenderProducts | BridgingLenderProducts>(
      `Product/GetAllProducts/${type}?uploadType=${uploadType}`,
    );
  }

  public getScheduledBridgingProducts(scheduleStart: string, uploadType: ProductUploadType): Observable<BridgingLenderProducts> {
    return this.getScheduledProducts(ProductCollectionType.Bridging, scheduleStart, uploadType) as Observable<BridgingLenderProducts>;
  }

  public getScheduledResAndBtlProducts(scheduleStart: string, uploadType: ProductUploadType): Observable<MatchedLenderProducts> {
    return this.getScheduledProducts(ProductCollectionType.ResiBtl, scheduleStart, uploadType) as Observable<MatchedLenderProducts>;
  }

  public getScheduledProducts(
    type: ProductCollectionType,
    scheduleStart: string,
    uploadType: ProductUploadType,
  ): Observable<MatchedLenderProducts | BridgingLenderProducts> {
    scheduleStart = scheduleStart ? format(new Date(scheduleStart), 'yyyy-MM-dd HH:mm') : '';
    return this.lenderHubDataService.get<MatchedLenderProducts | BridgingLenderProducts>(
      `Product/GetScheduledProducts/${type}?scheduleDate=${scheduleStart}&uploadType=${uploadType}`,
    );
  }

  public getScheduled(
    type: ProductCollectionType,
    uploadType: ProductUploadType,
  ): Observable<Record<'current' | 'future', Schedule[] | undefined>> {
    return this.lenderHubDataService
      .get<Schedule[]>(`Product/GetScheduled/${type}/?uploadType=${uploadType}`, { loading: false })
      .pipe(map(schedules => this.findCurrentFutureSchedules(schedules)));
  }

  public getHasLiveProducts(type: ProductCollectionType, uploadType: ProductUploadType): Observable<boolean> {
    return this.lenderHubDataService.get<boolean>(`Product/HasLiveProducts/${type}?uploadType=${uploadType}`, { loading: false });
  }

  public get visibleProductLendingTypes(): Observable<ProductsVisibilityState[]> {
    return this.lenderHubDataService.get<ProductsVisibilityState[]>('Product/Visibility');
  }

  public setProductLendingTypeVisible(lendingType: string, showProducts: boolean) {
    return this.lenderHubDataService.post<unknown>(`Product/Visibility/${lendingType}`, { showProducts });
  }

  public getValuationFeeScales(lendingTypeCode: string) {
    return this.lenderHubDataService.get<ValuationFeeScale[]>(`Product/ValuationFeeScale/${lendingTypeCode}`);
  }

  public getValuationFeeScalesByLenderNameId(lendingTypeCode: string, lenderNameId: number) {
    return this.lenderHubDataService.get<ValuationFeeScale[]>(`Product/ValuationFeeScale/${lendingTypeCode}/${lenderNameId}`);
  }

  public createValuationFeeScale(lendingTypeCode: string, feeScaleUpdateRequest: ValuationFeeScaleUpdateRequest) {
    return this.lenderHubDataService.post(`Product/ValuationFeeScale/Create/${lendingTypeCode}`, feeScaleUpdateRequest);
  }

  public createValuationFeeScaleByLenderNameId(
    lendingTypeCode: string,
    lenderNameId: number,
    feeScaleUpdateRequest: ValuationFeeScaleUpdateRequest,
  ) {
    return this.lenderHubDataService.post(`Product/ValuationFeeScale/Create/${lendingTypeCode}/${lenderNameId}`, feeScaleUpdateRequest);
  }

  public updateValuationFeeScale(lendingTypeCode: string, feeScaleUpdateRequest: ValuationFeeScaleUpdateRequest) {
    return this.lenderHubDataService.post(`Product/ValuationFeeScale/${lendingTypeCode}`, feeScaleUpdateRequest);
  }

  public updateValuationFeeScaleByLenderNameId(
    lendingTypeCode: string,
    lenderNameId: number,
    feeScaleUpdateRequest: ValuationFeeScaleUpdateRequest,
  ) {
    return this.lenderHubDataService.post(`Product/ValuationFeeScale/${lendingTypeCode}/${lenderNameId}`, feeScaleUpdateRequest);
  }

  public updateScheduledValuationFeeScaleDate(
    lendingTypeCode: string,
    scheduledValuationFeeScaleDateUpdateRequest: ScheduledValuationFeeScaleDateUpdateRequest,
  ) {
    return this.lenderHubDataService.put(
      `Product/ValuationFeeScale/${lendingTypeCode}/Schedule`,
      scheduledValuationFeeScaleDateUpdateRequest,
    );
  }

  public updateScheduledValuationFeeScaleDateByLenderNameId(
    lendingTypeCode: string,
    lenderNameId: number,
    scheduledValuationFeeScaleDateUpdateRequest: ScheduledValuationFeeScaleDateUpdateRequest,
  ) {
    return this.lenderHubDataService.put(
      `Product/ValuationFeeScale/${lendingTypeCode}/${lenderNameId}/Schedule`,
      scheduledValuationFeeScaleDateUpdateRequest,
    );
  }

  public deleteScheduledValuationFeeScale(lendingTypeCode: string, condition: FeeScaleConditionType) {
    return this.lenderHubDataService.delete(`Product/ValuationFeeScale/${lendingTypeCode}/Schedule/${condition}`);
  }

  public deleteScheduledValuationFeeScaleByLenderNameId(lendingTypeCode: string, lenderNameId: number, condition: FeeScaleConditionType) {
    return this.lenderHubDataService.delete(`Product/ValuationFeeScale/${lendingTypeCode}/${lenderNameId}/Schedule/${condition}`);
  }

  public findCurrentFutureSchedules(schedules: Schedule[] | undefined): Record<'current' | 'future', Schedule[] | undefined> {
    return {
      current: schedules?.filter(s => s.startTime && isBefore(parseISO(s.startTime), Date.now())),
      future: schedules?.filter(s => s.startTime && isAfter(parseISO(s.startTime), Date.now())),
    };
  }

  public getVariableRateCodes(opt?: ILoadingOptions): Observable<VariableRateCodeLookupViewModel> {
    return this.lenderHubDataService.get<VariableRateCodeLookupViewModel>('Product/VariableRateCodes', opt);
  }

  public getVariableRateCodesByLenderNameId(lenderNameId: number): Observable<VariableRateCodeLookupViewModel> {
    return this.lenderHubDataService.get<VariableRateCodeLookupViewModel>(`Product/VariableRateCodes/${lenderNameId}`);
  }

  public updateVariableRateCodes(req: VariableRateCodeLookupUpdateRequest): Observable<void> {
    return this.lenderHubDataService.post('Product/VariableRateCodes', req);
  }

  public getTrackerRateType(opt?: ILoadingOptions): Observable<TrackerProductRateType> {
    return this.lenderHubDataService.get<TrackerProductRateType>('Product/TrackerRateType', opt);
  }

  public getTrackerRateTypeByLenderNameId(lenderNameId: number): Observable<TrackerProductRateType> {
    return this.lenderHubDataService.get<TrackerProductRateType>(`Product/TrackerRateType/${lenderNameId}`);
  }

  public getBankOfEnglandBaseRate(opt?: ILoadingOptions): Observable<number> {
    return this.lenderHubDataService.get<number>('Product/BankOfEnglandBaseRate', opt);
  }

  public updateVariableRateCodesByLenderNameId(lenderNameId: number, req: VariableRateCodeLookupUpdateRequest): Observable<void> {
    return this.lenderHubDataService.post(`Product/VariableRateCodes/${lenderNameId}`, req);
  }

  public isVariableCodeInUse(variableRateCode: string): Observable<VariableRateCodeUsageViewModel> {
    return this.lenderHubDataService.get<VariableRateCodeUsageViewModel>(
      `Product/VariableRateCodes/HasExistingProducts/${encodeURIComponent(variableRateCode)}`,
    );
  }

  public isVariableCodeInUseByLenderNameId(variableRateCode: string, lenderNameId: number): Observable<VariableRateCodeUsageViewModel> {
    return this.lenderHubDataService.get<VariableRateCodeUsageViewModel>(
      `Product/VariableRateCodes/HasExistingProducts/${encodeURIComponent(variableRateCode)}/${lenderNameId}`,
    );
  }

  public deleteScheduledVariableRateCodes(): Observable<void> {
    return this.lenderHubDataService.delete('Product/VariableRateCodes/Scheduled');
  }

  public getPackagerVariableRateCodes() {
    return this.lenderHubDataService.get<PackagerVariableRateCodeViewModel[]>('Product/PackagerVariableRateCodes');
  }

  public getVariableRateConfig(): Observable<VariableRateConfigLookupViewModel> {
    return this.lenderHubDataService.get<VariableRateConfigLookupViewModel>('Product/VariableRateCodes/Config');
  }

  public getVariableRateConfigByLenderNameId(lenderNameId: number): Observable<VariableRateConfigLookupViewModel> {
    return this.lenderHubDataService.get<VariableRateConfigLookupViewModel>(`Product/VariableRateCodes/Config/${lenderNameId}`);
  }

  public updateVariableRateConfig(req: VariableRateConfigLookupUpdateRequest): Observable<void> {
    return this.lenderHubDataService.post('Product/VariableRateCodes/Config', req);
  }

  public updateVariableRateConfigByLenderNameId(lenderNameId: number, req: VariableRateConfigLookupUpdateRequest): Observable<void> {
    return this.lenderHubDataService.post(`Product/VariableRateCodes/Config/${lenderNameId}`, req);
  }
}
